<template>
  <div class="m-auto w-full max-w-xl p-4 pt-14">
    <template v-if="!npsSurvey.isError.value">
      <h1 v-if="npsSurvey.data.value" class="text mb-8 text-pretty text-center text-lg">
        {{ t('headline') }}
      </h1>
      <div v-else class="mx-auto mb-8 h-8 w-96 max-w-full animate-pulse rounded-lg bg-silver-100" />
      <div class="-mb-12 flex h-32 justify-center">
        <Transition
          v-if="npsSurvey.data.value?.car?.imageUrl"
          enter-from-class="opacity-0 translate-y-4"
          enter-active-class="transition-all duration-1000"
          appear
        >
          <img
            v-if="npsSurvey.data.value?.car?.imageUrl"
            class="h-32 w-auto"
            :src="npsSurvey.data.value?.car.imageUrl"
          />
        </Transition>
        <div v-else class="mt-16">
          <h2>{{ npsSurvey.data.value?.car?.make }} {{ npsSurvey.data.value?.car?.model }}</h2>
        </div>
      </div>
      <div
        ref="outerElement"
        class="content- box-content rounded-lg bg-silver-100 p-4 pt-32 transition-all"
      >
        <div ref="innerElement">
          <div
            v-if="completeNpsSurvey.isSuccess.value || npsSurvey.data.value?.status === 'COMPLETED'"
            class="pb-4"
          >
            <h2 class="mb-8 text-center text-black">{{ t('thanks') }}</h2>
            <div v-if="showReviewCtas" class="flex flex-col items-center gap-4">
              <div class="text-balance text-center text-black">
                {{ t('thankYouReview') }}
              </div>
              <a
                :href="t('trustpilotLink')"
                target="_blank"
                rel="noopener noreferrer"
                class="color-primary text-md flex w-full flex-row items-center justify-center gap-2 rounded-full bg-black px-4 py-2 font-bold uppercase text-gold-500 transition-all hover:bg-gray-800"
              >
                <StarIcon class="size-4" />
                {{ t('trustpilotCta') }}
              </a>
              <a
                v-if="googleMapsReviewCtaUrl"
                :href="googleMapsReviewCtaUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="color-primary text-md flex w-full flex-row items-center justify-center gap-2 rounded-full bg-black px-4 py-2 font-bold uppercase text-gold-500 transition-all hover:bg-gray-800"
              >
                <MapPinIcon class="size-4" />
                {{ t('googleMapsCta') }}
              </a>
            </div>
            <div v-else class="text-balance text-center text-black">
              {{ getThankYouMessage() }}
            </div>
          </div>
          <template v-else>
            <div class="mb-6 text-balance text-center font-medium">{{ t('question') }}</div>

            <!-- NPS Score Selection (0-10) -->
            <div class="mb-4 flex justify-center gap-1">
              <button
                v-for="i in 11"
                :key="i - 1"
                class="flex h-8 w-8 items-center justify-center rounded-md text-sm font-bold transition-all"
                :class="getScoreButtonClass(i - 1)"
                :title="t('scoreTitle', { score: i - 1 })"
                @click="score = i - 1"
              >
                {{ i - 1 }}
              </button>
            </div>

            <!-- Scale Labels -->
            <div class="mb-6 flex justify-between text-xs text-gray-500">
              <span>{{ t('notLikely') }}</span>
              <span>{{ t('veryLikely') }}</span>
            </div>

            <!-- Follow-up question based on score category -->
            <div v-if="score !== null" class="mb-4">
              <div class="mb-2 text-balance text-center text-sm font-medium text-gray-700">
                {{ getFollowUpQuestion() }}
              </div>
              <textarea
                v-model="comment"
                :placeholder="t('commentPlaceholder')"
                class="mb-4 w-full rounded-lg bg-silver-50 p-4 focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-gold-500"
              />

              <!-- Wished car question -->
              <div class="mb-2 text-balance text-center text-sm font-medium text-gray-700">
                {{ t('wishedCarQuestion') }}
              </div>
              <input
                v-model="wishedCar"
                type="text"
                :placeholder="t('wishedCarPlaceholder')"
                class="mb-4 w-full rounded-lg bg-silver-50 p-4 focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-gold-500"
              />
            </div>

            <button
              ref="button"
              class="color-primary text-md flex w-full flex-row items-center justify-center gap-2 rounded-full bg-black px-4 py-2 font-bold uppercase text-gold-500 transition-all enabled:active:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="score === null || npsSurvey.isLoading.value"
              @click="handleComplete"
            >
              <Transition
                enter-active-class="transition-all"
                enter-from-class="w-0"
                enter-to-class="w-4"
                exit-active-class="transition-all"
                exit-from-class="w-4"
                exit-to-class="w-0"
              >
                <div v-if="completeNpsSurvey.isPending.value" class="overflow-hidden">
                  <Spinner class="h-4 w-4" />
                </div>
              </Transition>
              {{ t('submit') }}
            </button>
          </template>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="mb-8 flex flex-col gap-4 text-center">
        <h2>{{ t('error') }}</h2>
        <p>{{ t('errorDescription') }}</p>
      </div>
    </template>
    <div class="mt-4 text-center text-xs">
      <a :href="t('imprintLink')" class="text-gray-700">{{ t('imprint') }}</a>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import confetti from 'canvas-confetti';
import { StarIcon, MapPinIcon } from 'lucide-vue-next';
import { useNpsSurvey, useCompleteNpsSurvey } from '@/queries/use-nps-surveys';
import { useRoute } from 'vue-router';
import { createToast } from 'mosha-vue-toastify';

const { t, locale } = useI18n();
const score = ref<number | null>(null);
const innerElement = ref<HTMLDivElement>();
const outerElement = ref<HTMLDivElement>();
const button = ref<HTMLButtonElement>();
const route = useRoute();

// Set locale from query parameter if provided (from email link)
const queryLocale = route.query.locale as string | undefined;
if (queryLocale && ['de', 'en', 'es'].includes(queryLocale)) {
  locale.value = queryLocale;
}
const npsSurvey = useNpsSurvey(route.params.id as string, route.query.key as string);
const completeNpsSurvey = useCompleteNpsSurvey(
  (vars) => {
    if (vars.survey.score >= 9) {
      confetti({
        origin: {
          x: (button.value!.offsetLeft + button.value!.offsetWidth / 2) / window.innerWidth,
          y: (button.value!.offsetTop + button.value!.offsetHeight / 2) / window.innerHeight,
        },
        colors: ['#CDAF74'],
      });
    }
  },
  () => {
    createToast(t('errorDescription'), {
      position: 'bottom-center',
      type: 'danger',
    });
  },
);
const comment = ref('');
const wishedCar = ref('');

// Helper function to compute score category from a score value
function getScoreCategoryFromScore(
  scoreVal: number | null | undefined,
): 'detractor' | 'passive' | 'promoter' | null {
  if (scoreVal === null || scoreVal === undefined) return null;
  if (scoreVal <= 6) return 'detractor';
  if (scoreVal <= 8) return 'passive';
  return 'promoter';
}

// Score category computed from current selection (for follow-up questions during survey)
const scoreCategory = computed(() => getScoreCategoryFromScore(score.value));

// Actual submitted score - uses the server score when the survey is completed so the thank-you
// message (and review CTAs) reflect what was submitted, not a leftover URL parameter.
const submittedScore = computed(() => {
  if (npsSurvey.data.value?.status === 'COMPLETED' && npsSurvey.data.value?.score != null) {
    return npsSurvey.data.value.score;
  }
  return score.value;
});

const submittedScoreCategory = computed(() => getScoreCategoryFromScore(submittedScore.value));

const showReviewCtas = computed(() => submittedScore.value != null && submittedScore.value >= 7);
const googleMapsReviewCtaUrl = computed(() => npsSurvey.data.value?.googleMapsReviewCtaUrl || null);

// Prefill score from query parameter (e.g., /nps-survey/:id?key=...&score=9)
const initialScore = Number(route.query.score);
if (!Number.isNaN(initialScore) && initialScore >= 0 && initialScore <= 10) {
  score.value = initialScore;
}

useResizeObserver(innerElement, (entries) => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  outerElement.value!.style.height = `${height}px`;
});

function getScoreButtonClass(s: number): string {
  const isSelected = score.value === s;
  return isSelected
    ? 'text-hazel-400 bg-obsidian-900 hover:bg-obsidian-700'
    : ' text-obsidian-900 bg-hazel-300 hover:bg-hazel-200';
}

function getFollowUpQuestion(): string {
  switch (scoreCategory.value) {
    case 'detractor':
      return t('followUpDetractor');
    case 'passive':
      return t('followUpPassive');
    case 'promoter':
      return t('followUpPromoter');
    default:
      return '';
  }
}

function getThankYouMessage(): string {
  if (submittedScoreCategory.value === 'detractor') {
    return t('thankYouDetractor');
  }
  return t('thankYouGeneric');
}

function handleComplete() {
  if (score.value === null) return;
  completeNpsSurvey.mutate({
    id: route.params.id as string,
    key: route.query.key as string,
    survey: {
      score: score.value,
      comment: comment.value || null,
      wishedCar: wishedCar.value || null,
    },
  });
}
</script>
<i18n lang="json">
{
  "en": {
    "headline": "How was your CarVia experience?",
    "question": "How likely are you to recommend CarVia to a friend or colleague?",
    "notLikely": "Not at all likely",
    "veryLikely": "Very likely",
    "scoreTitle": "{score} out of 10",
    "commentPlaceholder": "Your feedback (optional)",
    "followUpDetractor": "What went wrong? Where did we disappoint you?",
    "followUpPassive": "What was missing to give us a 9 or 10? What could we have done better?",
    "followUpPromoter": "What did you particularly enjoy? What should we definitely keep doing?",
    "wishedCarQuestion": "Which car would you like to see in the CarVia fleet or would you like to drive next?",
    "wishedCarPlaceholder": "Your car wish (optional)",
    "submit": "Submit Feedback",
    "thanks": "Thank you for your feedback!",
    "thankYouGeneric": "Your feedback helps us improve our service.",
    "thankYouDetractor": "We're sorry to hear about your experience. We'll use your feedback to improve.",
    "thankYouReview": "We're thrilled you enjoyed your experience! We would also be delighted if you left a review.",
    "trustpilotCta": "Leave a review on Trustpilot",
    "trustpilotLink": "https://www.trustpilot.com/evaluate/www.carvia.de",
    "googleMapsCta": "Leave a review on Google",
    "imprint": "Imprint",
    "imprintLink": "https://www.carvia.com/imprint/",
    "error": "Error",
    "errorDescription": "Unfortunately an error occurred. Please try again later."
  },
  "de": {
    "headline": "Wie war Ihre CarVia Erfahrung?",
    "question": "Wie wahrscheinlich ist es, dass Sie CarVia einem Freund oder Kollegen weiterempfehlen?",
    "notLikely": "Ausgeschlossen",
    "veryLikely": "Sehr wahrscheinlich",
    "scoreTitle": "{score} von 10",
    "commentPlaceholder": "Ihr Feedback (optional)",
    "followUpDetractor": "Was ist schiefgelaufen? Wo haben wir Sie enttäuscht?",
    "followUpPassive": "Was hat gefehlt, um eine 9 oder 10 zu vergeben? Was hätten wir besser machen können?",
    "followUpPromoter": "Was hat Ihnen besonders gut gefallen? Was sollten wir unbedingt beibehalten?",
    "wishedCarQuestion": "Welches Auto wünschen Sie sich zur CarVia Flotte oder würden Sie gerne als nächstes fahren?",
    "wishedCarPlaceholder": "Ihr Autowunsch (optional)",
    "submit": "Feedback absenden",
    "thanks": "Vielen Dank für Ihr Feedback!",
    "thankYouGeneric": "Ihr Feedback hilft uns, unseren Service zu verbessern.",
    "thankYouDetractor": "Es tut uns leid, von Ihrer Erfahrung zu hören. Wir werden Ihr Feedback nutzen, um uns zu verbessern.",
    "thankYouReview": "Wir freuen uns sehr, dass Ihnen Ihre Erfahrung gefallen hat! Über eine Bewertung würden wir uns ebenfalls sehr freuen.",
    "trustpilotCta": "Jetzt bei Trustpilot bewerten",
    "trustpilotLink": "https://de.trustpilot.com/evaluate/www.carvia.de",
    "googleMapsCta": "Jetzt bei Google bewerten",
    "imprint": "Impressum",
    "imprintLink": "https://www.carvia.de/impressum/",
    "error": "Fehler",
    "errorDescription": "Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
  },
  "es": {
    "headline": "¿Cómo fue su experiencia con CarVia?",
    "question": "¿Qué probabilidad hay de que recomiende CarVia a un amigo o colega?",
    "notLikely": "Nada probable",
    "veryLikely": "Muy probable",
    "scoreTitle": "{score} de 10",
    "commentPlaceholder": "Su comentario (opcional)",
    "followUpDetractor": "¿Qué salió mal? ¿Dónde le decepcionamos?",
    "followUpPassive": "¿Qué faltó para darnos un 9 o 10? ¿Qué podríamos haber hecho mejor?",
    "followUpPromoter": "¿Qué le gustó especialmente? ¿Qué deberíamos mantener sin falta?",
    "wishedCarQuestion": "¿Qué coche le gustaría ver en la flota CarVia o conducir la próxima vez?",
    "wishedCarPlaceholder": "Su deseo de modelo (opcional)",
    "submit": "Enviar comentario",
    "thanks": "¡Gracias por su comentario!",
    "thankYouGeneric": "Su opinión nos ayuda a mejorar el servicio.",
    "thankYouDetractor": "Lamentamos su experiencia. Usaremos su comentario para mejorar.",
    "thankYouReview": "¡Nos alegra que disfrutara la experiencia! También nos alegraría mucho una valoración.",
    "trustpilotCta": "Dejar una valoración en Trustpilot",
    "trustpilotLink": "https://es.trustpilot.com/evaluate/www.carvia.de",
    "googleMapsCta": "Dejar una valoración en Google",
    "imprint": "Aviso legal",
    "imprintLink": "https://www.carvia.com/imprint/",
    "error": "Error",
    "errorDescription": "Ha ocurrido un error. Inténtelo de nuevo más tarde."
  }
}
</i18n>
